import { useMemo } from 'react';
import { QueryClient, useQuery, useQueryClient } from 'react-query';
import { ARTICLE_STORE_CACHE_KEY } from '../../hooks/useArticleStore';
import useContext from '../../hooks/useContext';
import { SiteContext } from '../../SiteContext';
import { get } from '../fetch';
import { appendPreviewDate } from '../helper';

interface IResponse {
  variants: IProduct[];
}

const CACHE_KEY = 'articles';

const hitCache: Record<string, IProduct[]> = {};

const resolveCache = (qc: QueryClient, ids?: string[]): [IProduct[], string[]] => {
  const hit: IProduct[] = [];
  const miss: string[] = [];

  ids?.forEach((id) => {
    const cached = qc.getQueryData<IProduct>([CACHE_KEY, id]);

    if (cached) {
      hit.push(cached);
    } else {
      miss.push(id);
    }
  });

  const hitKey = hit
    .map((h) => h.id)
    .sort()
    .join(',');

  if (!hitCache[hitKey]) {
    hitCache[hitKey] = hit;
  }

  return [hitCache[hitKey], miss];
};

export const useGetArticles = (ids?: string[], extended = true) => {
  const {
    data: { searchServiceUrl },
  } = useContext(SiteContext);
  const queryClient = useQueryClient();
  const [hits, miss] = resolveCache(queryClient, ids);
  const cacheKey = miss.sort();
  const url = appendPreviewDate(
    `${searchServiceUrl}/variants?id=${miss.join('&id=')}&extended=${extended}`,
  );

  const { data, isLoading } = useQuery(['articles', ...cacheKey], () => get<IResponse>(url), {
    enabled: !!miss.length,
    onSuccess: (response: IResponse) => {
      // Workaround, this is hit as object sometimes
      if (response?.variants?.forEach) {
        response?.variants.forEach((article) => {
          queryClient.setQueryData([CACHE_KEY, article.id], article);
          queryClient.setQueryData([ARTICLE_STORE_CACHE_KEY, article.id], article);

          if (article.m3Id) {
            queryClient.setQueryData([CACHE_KEY, article.m3Id], article);
            queryClient.setQueryData([ARTICLE_STORE_CACHE_KEY, article.m3Id], article);
          }
        });
        miss
          .filter((id) => !response?.variants.find((a) => a.id === id || a.m3Id === id))
          .forEach((id) => {
            queryClient.setQueryData([CACHE_KEY, id], null);
            queryClient.setQueryData([ARTICLE_STORE_CACHE_KEY, id], null);
          });
      }
    },
  });

  return {
    articles: useMemo(() => (data?.variants || []).concat(hits), [data, hits]),
    loading: isLoading,
  };
};
